import { useTranslation } from "react-i18next";
import {
  BlockStack,
  Button,
  Card,
  InlineGrid,
  Text,
  ResourceList,
  ResourceItem,
  Avatar,
} from "@shopify/polaris";
import { Contact } from "types/Contact";
import { PlusIcon } from "@shopify/polaris-icons";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import { useAppDispatch } from "hooks";

type Props = {
  contacts: Contact[];
  customer: string;
};

// A tap on the phone or email must call or write, not also open the edit form
// that the whole card opens; on a phone the link is a full-height tap target.
const ContactLink = ({ href, children }: { href: string; children: string }) => (
  <a
    href={href}
    className="inline-flex items-center max-md:min-h-11"
    onClick={(event) => event.stopPropagation()}
  >
    {children}
  </a>
);

const Contacts = ({ contacts, customer }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const handleContactCreate = () => {
    dispatch(setModalxComponent("ContactForm"));
    dispatch(setModalxComponentProps({ customer }));
  };

  const handleContactEdit = (id: string) => {
    dispatch(setModalxComponent("ContactForm"));
    dispatch(setModalxComponentProps({ customer, id }));
  };

  return (
    <Card roundedAbove="sm">
      <BlockStack gap="200">
        <InlineGrid columns="1fr auto">
          <Text as="h2" variant="headingSm">
            {t("contacts")}
          </Text>
          <Button
            onClick={handleContactCreate}
            accessibilityLabel={t("contact_add")}
            icon={PlusIcon as any}
          >
            {t("contact_add")}
          </Button>
        </InlineGrid>
        <ResourceList
          resourceName={{ singular: t("contact"), plural: t("contacts") }}
          items={contacts}
          renderItem={(item) => {
            const { name, phone, email, position, id, isDisabled, linkedin, whatsapp, viber } =
              item;
            const media = <Avatar customer size="md" name={name} />;

            return (
              <ResourceItem
                id={id}
                onClick={handleContactEdit}
                media={media}
                accessibilityLabel={`View details for ${name}`}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <div>
                    <span
                      style={
                        isDisabled
                          ? { textDecoration: "line-through" }
                          : undefined
                      }
                    >
                      <Text variant="bodyMd" fontWeight="bold" as="h3">
                        {name}
                      </Text>
                    </span>
                    <div>{position}</div>
                    {phone && (
                      <div>
                        <ContactLink href={`tel:${phone}`}>{phone}</ContactLink>
                      </div>
                    )}
                    {email && (
                      <div>
                        <ContactLink href={`mailto:${email}`}>{email}</ContactLink>
                      </div>
                    )}
                    {linkedin && <div>{t("linkedin", { defaultValue: "LinkedIn" })}: {linkedin}</div>}
                    {whatsapp && <div>{t("whatsapp", { defaultValue: "WhatsApp" })}: {whatsapp}</div>}
                    {viber && <div>{t("viber", { defaultValue: "Viber" })}: {viber}</div>}
                  </div>
                  {isDisabled && (
                    <Text
                      variant="bodySm"
                      tone="critical"
                      fontWeight="semibold"
                      as="span"
                    >
                      {t("disabled")}
                    </Text>
                  )}
                </div>
              </ResourceItem>
            );
          }}
        />
      </BlockStack>
    </Card>
  );
};

export default Contacts;
